import { apiClient, API_CONFIG } from '@/api/core';
import { buildQueryString } from '@/api/shared';
import type { ApiResponse , LC_PagedResult } from '@/api/shared';

import type {
  LC_OpenCutYearOut,
  LC_OpenCutMonthOut,
  LC_OpenCutDescriptionOut,
  LC_RecalculationProcessStatusOut,
  LC_RecalculationProcessStartIn,
  LC_ProcessHistoryItemOut,
  LC_ProcessHistoryQueryParams,
  RecalculationProcessParams,
  RecalculationProcessStatus,
  ProcessHistoryItem,
  OpenCutYear,
  OpenCutMonth,
  OpenCutDescription,
} from '../types';
import {
  mapOpenCutYear,
  mapOpenCutMonth,
  mapOpenCutDescription,
  mapRecalculationProcessStatus,
  mapProcessHistoryItem,
} from '../mappers';


export class RecalculationMonthlyCutsService {
  async getOpenYears(signal?: AbortSignal): Promise<ApiResponse<OpenCutYear[]>> {
    const response = await apiClient.get<LC_OpenCutYearOut[]>(
      API_CONFIG.ENDPOINTS.RECALCULATION.OPEN_YEARS,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: response.data.map(mapOpenCutYear) };
  }

  async getOpenMonths(
    year: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<OpenCutMonth[]>> {
    const queryString = buildQueryString({ year });
    const response = await apiClient.get<LC_OpenCutMonthOut[]>(
      `${API_CONFIG.ENDPOINTS.RECALCULATION.OPEN_MONTHS}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: response.data.map(mapOpenCutMonth) };
  }

  async getOpenDescriptions(
    year: number,
    month: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<OpenCutDescription[]>> {
    const queryString = buildQueryString({ year, month });
    const response = await apiClient.get<LC_OpenCutDescriptionOut[]>(
      `${API_CONFIG.ENDPOINTS.RECALCULATION.OPEN_DESCRIPTIONS}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: response.data.map(mapOpenCutDescription) };
  }

  async getProcessStatus(
    signal?: AbortSignal,
  ): Promise<ApiResponse<RecalculationProcessStatus>> {
    const response = await apiClient.get<LC_RecalculationProcessStatusOut>(
      API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_STATUS,
      { signal },
    );

    if (response.success && response.data) {
      return { success: true, data: mapRecalculationProcessStatus(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async startProcess(
    params: RecalculationProcessParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<null>> {
    const toCsvOrNull = (values: string[]): string | null => (values.length > 0 ? values.join(',') : null);

    const body: LC_RecalculationProcessStartIn = {
      monthlyCutId: params.monthlyCutId,
      employeeIds: params.employeeIds ? params.employeeIds.join(',') : null,
      companyIds: toCsvOrNull(params.companyIds),
      areaIds: toCsvOrNull(params.areaIds),
      positionIds: toCsvOrNull(params.positionIds),
      costCenterIds: toCsvOrNull(params.costCenterIds),
      locationIds: toCsvOrNull(params.locationIds),
      contractModalityIds: toCsvOrNull(params.contractModalityIds),
      groupIds: toCsvOrNull(params.groupIds),
      employeeStatus: params.employeeStatus || null,
    };

    const response = await apiClient.post<null>(
      API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_START,
      body,
      { signal },
    );

    return { success: response.success, data: null, error: response.error };
  }

  async stopProcess(signal?: AbortSignal): Promise<ApiResponse<null>> {
    const response = await apiClient.post<null>(
      API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_STOP,
      {},
      { signal },
    );

    return { success: response.success, data: null, error: response.error };
  }

  async downloadLastErrorReport(): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return apiClient.downloadBlobWithFilename(
      API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_LAST_ERRORS_FILE,
      { method: 'GET' },
    );
  }

  async downloadErrorReport(
    historyProcessId: number,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    // El backend identifica el proceso por `processId` (= IdRecalculoAsistencia),
    // que en el listado corresponde al campo `historyProcessId`.
    const queryString = buildQueryString({ processId: historyProcessId });
    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_HISTORY_ERRORS_FILE}${queryString}`,
      { method: 'GET' },
    );
  }

  async getProcessHistory(
    params: LC_ProcessHistoryQueryParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<{ items: ProcessHistoryItem[]; totalRecords: number }>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      ...(params.searchText ? { searchText: params.searchText } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.orderBy ? { orderBy: params.orderBy } : {}),
      ...(params.orderDirection ? { orderDirection: params.orderDirection } : {}),
    });

    const response = await apiClient.get<LC_PagedResult<LC_ProcessHistoryItemOut>>(
      `${API_CONFIG.ENDPOINTS.RECALCULATION.PROCESS_HISTORY}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    const items = (response.data.data ?? []).map(mapProcessHistoryItem);
    return {
      success: true,
      data: { items, totalRecords: response.data.pagination.totalRecords },
    };
  }
}

export const recalculationMonthlyCutsService = new RecalculationMonthlyCutsService();
